import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Backdrop,
  Grow,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  CloseRounded,
  ChildCare,
  Brush,
  School,
  EmojiObjects,
  EmojiEvents,
  Public,
  Favorite,
  VolunteerActivism,
  RocketLaunch,
  Code,
  WorkspacePremium,
} from "@mui/icons-material";
// Images
import BabyPic from "../../../../assets/images/experience/born-day.webp";
import JuniorSchool from "../../../../assets/images/experience/junior-design.jpg";
import University from "../../../../assets/images/experience/university.webp";
import IDesign from "../../../../assets/images/experience/idesign.webp";
import Hudco from "../../../../assets/images/experience/hudco.webp";
import LLA from "../../../../assets/images/experience/lla.webp";
import Dad from "../../../../assets/images/experience/dad.webp";
import Teaching from "../../../../assets/images/experience/teaching.webp";
import NeramLogo from "../../../../assets/images/experience/neram-logo.webp";
import TechMahindra from "../../../../assets/images/experience/Tech_Mahindra.jpeg";
import McmAchievement from "../../../../assets/images/experience/mcm_achievement.jpg";
// Small WebP versions for the 80px rail circles (scripts/optimize-home-images.mjs).
// The originals above are only shown large, in the detail modal.
import BabyPicThumb from "../../../../assets/images/experience/thumbs/born-day.webp";
import JuniorSchoolThumb from "../../../../assets/images/experience/thumbs/junior-design.webp";
import UniversityThumb from "../../../../assets/images/experience/thumbs/university.webp";
import IDesignThumb from "../../../../assets/images/experience/thumbs/idesign.webp";
import HudcoThumb from "../../../../assets/images/experience/thumbs/hudco.webp";
import LLAThumb from "../../../../assets/images/experience/thumbs/lla.webp";
import DadThumb from "../../../../assets/images/experience/thumbs/dad.webp";
import TeachingThumb from "../../../../assets/images/experience/thumbs/teaching.webp";
import NeramLogoThumb from "../../../../assets/images/experience/thumbs/neram-logo.webp";
import TechMahindraThumb from "../../../../assets/images/experience/thumbs/Tech_Mahindra.webp";
import McmAchievementThumb from "../../../../assets/images/experience/thumbs/mcm_achievement.webp";

// Full image -> rail thumbnail. The originals ran up to 2376px and 515 KB each,
// all fetched on load for 80px circles, and one cost a 180ms decode mid-scroll.
const RAIL_THUMBS = new Map([
  [BabyPic, BabyPicThumb],
  [JuniorSchool, JuniorSchoolThumb],
  [University, UniversityThumb],
  [IDesign, IDesignThumb],
  [Hudco, HudcoThumb],
  [LLA, LLAThumb],
  [Dad, DadThumb],
  [Teaching, TeachingThumb],
  [NeramLogo, NeramLogoThumb],
  [TechMahindra, TechMahindraThumb],
  [McmAchievement, McmAchievementThumb],
]);

// Rail geometry. `pitch` MUST equal TimelineItem's width + its two horizontal margins
// (`mx: 1.5` => 12px each side, `mx: 3` => 24px each side), and `pad` the container's `px`.
// The arrows step by exactly one pitch, so any drift between these numbers and
// TimelineItem's puts every item off-grid a little further with each click.
const RAIL = {
  mobile: { pitch: 224, pad: 32 }, // 200 + 2*12 ; px: 4
  desktop: { pitch: 308, pad: 64 }, // 260 + 2*24 ; px: 8
};

// Expo-out — the JS twin of the cubic-bezier(0.16, 1, 0.3, 1) used in NavbarMobile.jsx.
// Fast departure, long gentle settle: this is what reads as "slide" instead of "jump".
const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
const SCROLL_MS = 560;

const timelineData = [
  {
    year: 1993,
    title: "Humble Beginnings",
    description:
      "I grew up in Pudukkottai in an ordinary working-class family: a police officer and a nurse who taught me discipline and the value of doing things well…",
    image: BabyPic,
    icon: ChildCare,
    iconColor: "#fbc02d",
    moreDescription:
      "I grew up in Pudukkottai, a small town in southern India, in an ordinary working-class family. My father was a police officer and my mother a nurse: hard-working parents who built a simple, honest life and taught me discipline, patience, and the value of doing things well. That foundation still shapes how I work today.",
  },
  {
    year: 2008,
    title: "Where Design Began",
    description:
      "Design found me early: winning art competitions while topping my classes, finishing school with 98% in my board exams…",
    image: JuniorSchool,
    icon: Brush,
    iconColor: "#ff7043",
    moreDescription:
      "Design found me early. Through school I kept winning sketching and painting competitions while topping my classes, finishing with 98% in my board exams. That mix of creativity and rigor, of making things both beautiful and correct, is still exactly how I approach design and engineering.",
  },
  {
    year: 2011,
    title: "NIT Trichy",
    description:
      "A top national rank earned me a seat at NIT Trichy to study Architecture, where I learned to think in systems, the foundation of my UX work…",
    image: University,
    icon: School,
    iconColor: "#29b6f6",
    moreDescription:
      "A top rank in the national entrance exam earned me a place at NIT Trichy, one of India's most selective institutes, to study Architecture. Architecture taught me to think in systems: how people move through space, how constraints shape solutions, how every detail serves the whole. It's the foundation everything in my UX work is built on.",
  },
  {
    year: 2012,
    title: "Learning the Craft",
    description:
      "I entered national design competitions and took on paid design work, learning the craft, and learning to design for people, not just for looks…",
    image: IDesign,
    icon: EmojiObjects,
    iconColor: "#ffd54f",
    moreDescription:
      "I started entering national design competitions like iDesign, designing real products for real human problems, and took on paid graphic-design work in Photoshop and Illustrator to fund my studies. This is where I learned the craft, and where designing for people rather than just for looks became second nature.",
  },
  {
    year: 2014,
    title: "HUDCO Trophy",
    description:
      "My team won the national HUDCO Trophy designing shelters for fishing communities: real constraints, real users, the empathy I design with today…",
    image: Hudco,
    icon: EmojiEvents,
    iconColor: "#4dd0e1",
    moreDescription:
      "At the 56th NASA Convention my team won the national HUDCO Trophy for designing shelters for urban poor fishing communities. Designing within tight real-world constraints (budget, materials, dignity, daily life) for users very different from myself taught me the empathy and problem-framing I still rely on every day.",
  },
  {
    year: 2014,
    title: "Going Global",
    description:
      "An international internship in Kuala Lumpur: client presentations, cross-cultural teams, and a first taste of global design standards…",
    image: LLA,
    icon: Public,
    iconColor: "#7986cb",
    moreDescription:
      "I joined Lauren Lim Architects in Kuala Lumpur as a design intern, working alongside professionals from around the world. Leading client presentations and shaping design development in a cross-cultural team gave me an early sense of global standards, and the confidence to communicate design clearly to any audience.",
  },
  {
    year: 2015,
    title: "A Turning Point",
    description:
      "Losing my father reshaped everything. I stayed for my family, found resilience and responsibility, and resolved to build things that help people…",
    image: Dad,
    icon: Favorite,
    iconColor: "#ef5350",
    moreDescription:
      "My father passed away, and I turned down a placement in Bangalore to stay close and care for my family. It was the hardest chapter of my life, and the one that reshaped it. I came out of it with resilience and a deep sense of responsibility, and resolved to build a career around making things that genuinely help people.",
  },
  {
    year: 2016,
    title: "Teaching, Then Building",
    description:
      "I began connecting remote-village students with great teachers online, and discovered a love for building products…",
    image: Teaching,
    icon: VolunteerActivism,
    iconColor: "#81c784",
    moreDescription:
      "I started connecting students in remote villages with great teachers online. Seeing how the right tool could change a student's future turned a love of teaching into a love of building products. That spark became Neram Classes.",
  },
  {
    year: 2016,
    title: "Founder, Neram Classes",
    description:
      "I founded Neram Classes, designing and building an EdTech platform now reaching 1,000+ students a year, with a student achieving All India Rank 1…",
    image: NeramLogo,
    icon: RocketLaunch,
    iconColor: "#23d5ab",
    moreDescription:
      "I founded Neram Classes, an EdTech platform for architecture-entrance students, and designed and built it end to end. It now reaches 1,000+ students a year in Tamil and English across 50+ cities, draws 168K+ monthly pageviews, runs as a profitable business, and in 2024 one of our students achieved All India Rank 1 in JEE B.Arch. This is where I became a true product builder: design, code, and outcomes.",
  },
  {
    year: 2022,
    title: "Product Designer & Frontend Engineer · Tech Mahindra",
    description:
      "At Tech Mahindra I lead design and frontend for Vertex Microsystems and NEOM: a 50+ component design system, 500+ users, 60% faster workflows…",
    image: TechMahindra,
    imagePosition: "center 15%",
    icon: Code,
    iconColor: "#23a6d5",
    moreDescription:
      "At Tech Mahindra I lead design and frontend engineering for enterprise clients including Vertex Microsystems (semiconductor) and NEOM – The Line. I architected a 50+ component design system in Storybook, rebuilt complex legacy tools into modern React apps (cutting one core workflow's time by 60% for 500+ users and saving ~40% of front-end build time), and I mentor 15+ engineers. I'm the rare designer who both designs and ships production code.",
  },
  {
    year: 2025,
    title: "Recognized & Still Building",
    description:
      "Recognized with Team of the Quarter and back-to-back Pat on the Back awards. 10 years bridging design and engineering, now ready for what's next…",
    image: McmAchievement,
    imageZoom: 1.2,
    imageOrigin: "45% 50%",
    icon: WorkspacePremium,
    iconColor: "#ba68c8",
    moreDescription:
      "The work has been recognized along the way: Team of the Quarter for the change-management console redesign, and Pat on the Back awards in both 2024 and 2025. Ten years in, I bridge design and engineering, still ship my own products (like AESTA, running live on real construction sites), and I'm now looking for a senior product design / frontend engineering role where I can do my best work at scale.",
  },
];

const TimelineItem = ({ item, index, onClick, isMobile }) => {
  const isTop = index % 2 === 0;
  const IconComp = item.icon;
  const number = String(index + 1).padStart(2, "0");
  const hasRealImage = item.image && !item.image.includes("placehold");

  // Mobile-specific measurements
  const itemWidth = isMobile ? 200 : 260;
  const iconSize = isMobile ? 28 : 36;
  const imageSize = isMobile ? 60 : 80;
  const connectorOffset = isMobile ? "50px" : "65px";

  return (
    <Box
      sx={{
        position: "relative",
        width: itemWidth,
        flexShrink: 0,
        mx: { xs: 1.5, md: 3 },
        textAlign: "center",
        height: isMobile ? 280 : 340,
      }}
    >
      {/* Icon node on main line */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          p: { xs: 0.75, md: 1 },
          borderRadius: "50%",
          boxShadow: `0 4px 20px ${item.iconColor}40`,
          border: `3px solid ${item.iconColor}`,
          zIndex: 3,
          transition: "transform 0.3s, box-shadow 0.3s",
          "&:hover": {
            transform: "translate(-50%, -50%) scale(1.1)",
            boxShadow: `0 6px 28px ${item.iconColor}60`,
          },
        }}
      >
        <IconComp sx={{ color: item.iconColor, fontSize: iconSize }} />
      </Box>

      {/* Vertical connector from icon - extends toward content (130px) */}
      <Box
        sx={{
          position: "absolute",
          top: isTop ? "calc(50% - 130px)" : "50%",
          left: "50%",
          width: 2,
          height: "130px",
          background: `linear-gradient(${isTop ? "to bottom" : "to top"}, ${item.iconColor}20, ${item.iconColor})`,
          transform: "translateX(-50%)",
          zIndex: 1,
        }}
      />

      {/* Vertical connector - faded extension beyond icon */}
      <Box
        sx={{
          position: "absolute",
          top: isTop ? "50%" : `calc(50% - ${connectorOffset})`,
          left: "50%",
          width: 2,
          height: connectorOffset,
          background: `linear-gradient(${isTop ? "to bottom" : "to top"}, ${item.iconColor}, ${item.iconColor}20)`,
          transform: "translateX(-50%)",
          zIndex: 1,
        }}
      />

      {/* Horizontal connector to image */}
      <Box
        sx={{
          position: "absolute",
          top: isTop ? `calc(50% - ${connectorOffset})` : `calc(50% + ${connectorOffset})`,
          left: "50%",
          width: isMobile ? "35px" : "50px",
          height: 2,
          bgcolor: item.iconColor,
          transform: isTop ? "translateY(-1px)" : "translateY(-1px)",
          zIndex: 1,
        }}
      />

      {/* Image or Icon placeholder */}
      <Box
        onClick={() => onClick(item, index)}
        sx={{
          position: "absolute",
          top: isTop
            ? `calc(50% - ${connectorOffset} - ${imageSize / 2}px - ${isMobile ? "20px" : "30px"})`
            : `calc(50% + ${connectorOffset} - ${imageSize / 2}px + ${isMobile ? "20px" : "30px"})`,
          left: `calc(50% + ${isMobile ? "32px" : "45px"})`,
          width: imageSize,
          height: imageSize,
          borderRadius: "50%",
          cursor: "pointer",
          transition: "transform 0.3s, box-shadow 0.3s",
          overflow: "hidden",
          bgcolor: hasRealImage ? "transparent" : `${item.iconColor}15`,
          border: `3px solid ${item.iconColor}30`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          "&:hover": {
            transform: "scale(1.15)",
            boxShadow: `0 8px 24px ${item.iconColor}40`,
          },
        }}
      >
        {hasRealImage ? (
          <Box
            component="img"
            src={RAIL_THUMBS.get(item.image) || item.image}
            alt={item.title}
            loading="lazy"
            decoding="async"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: item.imagePosition || "center",
              transform: item.imageZoom ? `scale(${item.imageZoom})` : "none",
              transformOrigin: item.imageOrigin || "center",
            }}
          />
        ) : (
          <IconComp
            sx={{
              fontSize: isMobile ? 28 : 36,
              color: item.iconColor,
              opacity: 0.6,
            }}
          />
        )}
      </Box>

      {/* Number with glow effect */}
      <Box
        sx={{
          position: "absolute",
          top: isTop ? `calc(50% + ${isMobile ? "60px" : "85px"})` : `calc(50% - ${isMobile ? "90px" : "125px"})`,
          left: `calc(50% - ${isMobile ? "40px" : "50px"})`,
          width: 140,
          textAlign: "left",
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.5rem", md: "2rem" },
            fontFamily: "Roboto, sans-serif",
            color: `${item.iconColor}25`,
            textShadow: `0 0 20px ${item.iconColor}15`,
          }}
        >
          {number}
        </Typography>
      </Box>

      {/* Content box */}
      <Box
        sx={{
          position: "absolute",
          top: isTop ? `calc(50% + ${isMobile ? "25px" : "35px"})` : `calc(50% - ${isMobile ? "80px" : "110px"})`,
          left: `calc(50% + ${isMobile ? "10px" : "20px"})`,
          width: isMobile ? 120 : 160,
          textAlign: "left",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            fontSize: { xs: "0.9rem", md: "1.1rem" },
            color: "#1a1a1a",
            lineHeight: 1.2,
          }}
        >
          {item.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Kalam, cursive",
            fontSize: { xs: "0.7rem", md: "0.8rem" },
            color: "#666",
            mt: 0.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.4,
          }}
        >
          {item.description}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontWeight: 500,
            fontSize: { xs: "0.75rem", md: "0.85rem" },
            color: item.iconColor,
            mt: 0.5,
          }}
        >
          {item.year}
        </Typography>
      </Box>
    </Box>
  );
};

const EmploymentTimeline = () => {
  const scrollRef = useRef();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTouch = useMediaQuery("(pointer: coarse)");
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const { pitch, pad } = isMobile ? RAIL.mobile : RAIL.desktop;

  // In-flight tween: the rAF handle, and the destination it is heading for (null when idle).
  const rafRef = useRef(0);
  const targetRef = useRef(null);

  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Any real user input wins over the tween — otherwise it keeps writing scrollLeft
    // underneath them and the rail feels like it is fighting back.
    const cancelTween = () => {
      cancelAnimationFrame(rafRef.current);
      targetRef.current = null;
    };

    // Only a wheel gesture aimed *along* the rail counts as taking over. A plain vertical
    // wheel over this element scrolls the page, not the rail, so cancelling on it would
    // abort a tween the user never meant to interrupt.
    const onWheel = (e) => {
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) cancelTween();
    };

    el.addEventListener("scroll", checkScrollPosition);
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchstart", cancelTween, { passive: true });
    el.addEventListener("pointerdown", cancelTween, { passive: true });
    window.addEventListener("resize", checkScrollPosition);
    checkScrollPosition();

    return () => {
      el.removeEventListener("scroll", checkScrollPosition);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", cancelTween);
      el.removeEventListener("pointerdown", cancelTween);
      window.removeEventListener("resize", checkScrollPosition);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const animateTo = (to) => {
    const el = scrollRef.current;
    if (!el) return;

    const dest = Math.max(0, Math.min(to, el.scrollWidth - el.clientWidth));
    targetRef.current = dest;
    cancelAnimationFrame(rafRef.current);

    if (prefersReducedMotion) {
      el.scrollLeft = dest;
      targetRef.current = null;
      return;
    }

    const from = el.scrollLeft;
    const delta = dest - from;
    if (Math.abs(delta) < 1) {
      targetRef.current = null;
      return;
    }

    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / SCROLL_MS);
      el.scrollLeft = from + delta * easeOutExpo(t);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        targetRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const stepBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // Step from the in-flight target, not the live scrollLeft, so rapid clicks accumulate
    // (+1 item each) and the tween re-aims mid-flight instead of restarting or queueing.
    // Rounding re-derives the nearest grid index every click, so the rail self-corrects
    // even if a free scroll left it parked between two items.
    const base = targetRef.current ?? el.scrollLeft;
    animateTo((Math.round(base / pitch) + dir) * pitch);
  };

  const handleNext = () => stepBy(1);
  const handlePrev = () => stepBy(-1);

  const handleClick = (item, index) => {
    setCurrent(item);
    setCurrentIndex(index);
    setOpen(true);
  };

  const handleModalPrev = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setCurrent(timelineData[newIndex]);
    }
  };

  const handleModalNext = () => {
    if (currentIndex < timelineData.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setCurrent(timelineData[newIndex]);
    }
  };

  const hasRealImage = current.image && !current.image?.includes("placehold");
  const CurrentIcon = current.icon;
  const accent = current.iconColor || "#e73c7e";
  const total = timelineData.length;
  const chapterNo = String(currentIndex + 1).padStart(2, "0");
  const progressPct = total ? ((currentIndex + 1) / total) * 100 : 0;

  return (
    <Box sx={{ position: "relative", py: { xs: 4, md: 8 } }}>
      {/* Scrollable container with line INSIDE */}
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          overflowX: "auto",
          // No `scrollBehavior: "smooth"` on purpose: CSS smooth-scroll intercepts every
          // programmatic scrollLeft write and re-animates it, which makes the rAF tween in
          // animateTo() stutter. The two cannot both own this property.
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          px: { xs: 4, md: 8 },
          height: { xs: 280, md: 340 },
          position: "relative",
          alignItems: "center",
          // Fade the edges so items dissolve out behind the arrow buttons instead of being
          // hard-cut at the container edge. A mask rather than a solid overlay because the
          // section sits on TimeLine.jsx's animated gradient — there is no flat colour to
          // fade to. Widths clear the buttons (md: left 24 + 52 = 76; xs: 8 + 44 = 52).
          maskImage: {
            xs: "linear-gradient(to right, transparent 0, #000 60px, #000 calc(100% - 60px), transparent 100%)",
            md: "linear-gradient(to right, transparent 0, #000 88px, #000 calc(100% - 88px), transparent 100%)",
          },
          WebkitMaskImage: {
            xs: "linear-gradient(to right, transparent 0, #000 60px, #000 calc(100% - 60px), transparent 100%)",
            md: "linear-gradient(to right, transparent 0, #000 88px, #000 calc(100% - 88px), transparent 100%)",
          },
        }}
      >
        {/* Main horizontal line - NOW INSIDE scroll container */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            // Same `pitch` the arrows step by, plus the container's padding at both ends —
            // the old 230/310 literals matched neither the real pitch nor each other, so the
            // rule ran out ~100px before the last item on desktop.
            width: `${pad * 2 + timelineData.length * pitch}px`,
            height: 4,
            background: "linear-gradient(90deg, #333 0%, #666 50%, #333 100%)",
            transform: "translateY(-50%)",
            zIndex: 1,
            borderRadius: 2,
          }}
        />

        {timelineData.map((item, i) => (
          <TimelineItem
            key={i}
            item={item}
            index={i}
            onClick={handleClick}
            isMobile={isMobile}
          />
        ))}
      </Box>

      {/* Navigation buttons */}
      {!isTouch && (
        <>
          <IconButton
            onClick={handlePrev}
            disabled={!canScrollLeft}
            sx={{
              position: "absolute",
              top: "50%",
              left: { xs: 8, md: 24 },
              zIndex: 10,
              transform: "translateY(-50%)",
              bgcolor: "rgba(255,255,255,0.95)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              width: { xs: 44, md: 52 },
              height: { xs: 44, md: 52 },
              opacity: canScrollLeft ? 1 : 0.3,
              transition: "transform 0.3s ease, opacity 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                bgcolor: "white",
                transform: "translateY(-50%) scale(1.08)",
                boxShadow: "0 6px 28px rgba(0,0,0,0.2)",
              },
            }}
          >
            <ChevronLeft sx={{ fontSize: { xs: 28, md: 32 }, color: "#333" }} />
          </IconButton>

          <IconButton
            onClick={handleNext}
            disabled={!canScrollRight}
            sx={{
              position: "absolute",
              top: "50%",
              right: { xs: 8, md: 24 },
              zIndex: 10,
              transform: "translateY(-50%)",
              bgcolor: "rgba(255,255,255,0.95)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              width: { xs: 44, md: 52 },
              height: { xs: 44, md: 52 },
              opacity: canScrollRight ? 1 : 0.3,
              transition: "transform 0.3s ease, opacity 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                bgcolor: "white",
                transform: "translateY(-50%) scale(1.08)",
                boxShadow: "0 6px 28px rgba(0,0,0,0.2)",
              },
            }}
          >
            <ChevronRight sx={{ fontSize: { xs: 28, md: 32 }, color: "#333" }} />
          </IconButton>
        </>
      )}

      {/* Story Modal - refined editorial card */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: { timeout: 400, sx: { bgcolor: "rgba(0,0,0,0.85)" } },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Grow in={open} timeout={320}>
          <Box
            sx={{
              width: { xs: "100%", sm: 460, md: 560 },
              maxWidth: "100%",
              maxHeight: "90vh",
              bgcolor: "#fff",
              borderRadius: 4,
              boxShadow: "0 30px 90px rgba(0,0,0,0.45)",
              overflow: "hidden",
              outline: "none",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header - photo (with scrim) or icon placeholder, with overlaid meta */}
            <Box
              sx={{
                position: "relative",
                flexShrink: 0,
                width: "100%",
                height: { xs: 200, md: 240 },
                bgcolor: hasRealImage ? "#1a1a1a" : `${accent}14`,
                overflow: "hidden",
              }}
            >
              {/* Real photo - object-fit cover with per-image crop controls */}
              {hasRealImage && (
                <Box
                  component="img"
                  src={current.image}
                  alt={current.title}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: current.imagePosition || "center",
                    transform: current.imageZoom
                      ? `scale(${current.imageZoom})`
                      : "none",
                    transformOrigin: current.imageOrigin || "center",
                  }}
                />
              )}

              {/* Placeholder icon */}
              {!hasRealImage && CurrentIcon && (
                <CurrentIcon
                  sx={{
                    position: "absolute",
                    top: "42%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: { xs: 64, md: 80 },
                    color: accent,
                    opacity: 0.45,
                  }}
                />
              )}

              {/* Gradient scrim (only over a real photo, for legible overlay text) */}
              {hasRealImage && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.18) 50%, rgba(0,0,0,0) 100%)",
                  }}
                />
              )}

              {/* Overlaid chapter meta (bottom-left) */}
              <Box
                sx={{
                  position: "absolute",
                  left: { xs: 20, md: 24 },
                  bottom: { xs: 16, md: 20 },
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 3,
                    borderRadius: 2,
                    bgcolor: accent,
                    mb: 1.25,
                    boxShadow: hasRealImage
                      ? "0 1px 6px rgba(0,0,0,0.4)"
                      : "none",
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                    color: hasRealImage ? "rgba(255,255,255,0.92)" : accent,
                  }}
                >
                  Chapter {chapterNo}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 800,
                    fontSize: { xs: 30, md: 36 },
                    lineHeight: 1,
                    mt: 0.25,
                    color: hasRealImage ? "#fff" : "#1a1a1a",
                    textShadow: hasRealImage
                      ? "0 2px 14px rgba(0,0,0,0.55)"
                      : "none",
                  }}
                >
                  {current.year}
                </Typography>
              </Box>

              {/* Close button - refined frosted chip (legible on photo or tint) */}
              <IconButton
                onClick={() => setOpen(false)}
                aria-label="Close"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 3,
                  width: 40,
                  height: 40,
                  color: "#fff",
                  bgcolor: "rgba(17,17,17,0.55)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                  transition: "transform 0.25s ease, background-color 0.25s ease",
                  "&:hover": {
                    bgcolor: "rgba(17,17,17,0.62)",
                    transform: "rotate(90deg) scale(1.06)",
                  },
                }}
              >
                <CloseRounded sx={{ fontSize: 20 }} />
              </IconButton>

              {/* Navigation arrows - matching frosted chips */}
              {currentIndex > 0 && (
                <IconButton
                  onClick={handleModalPrev}
                  aria-label="Previous chapter"
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 3,
                    width: 40,
                    height: 40,
                    color: "#fff",
                    bgcolor: "rgba(17,17,17,0.55)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                    transition: "transform 0.25s ease, background-color 0.25s ease",
                    "&:hover": {
                      bgcolor: "rgba(17,17,17,0.62)",
                      transform: "translateY(-50%) scale(1.08)",
                    },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
              )}

              {currentIndex < total - 1 && (
                <IconButton
                  onClick={handleModalNext}
                  aria-label="Next chapter"
                  sx={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 3,
                    width: 40,
                    height: 40,
                    color: "#fff",
                    bgcolor: "rgba(17,17,17,0.55)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                    transition: "transform 0.25s ease, background-color 0.25s ease",
                    "&:hover": {
                      bgcolor: "rgba(17,17,17,0.62)",
                      transform: "translateY(-50%) scale(1.08)",
                    },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              )}
            </Box>

            {/* Content */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                bgcolor: "#fff",
                p: { xs: 3, md: 4 },
                overflowY: "auto",
              }}
            >
              <Typography
                component="h2"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: 24, md: 30 },
                  letterSpacing: "-0.5px",
                  lineHeight: 1.15,
                  color: "#1a1a1a",
                  m: 0,
                }}
              >
                {current.title}
              </Typography>

              {/* Accent underline */}
              <Box
                sx={{
                  mt: 1.25,
                  width: 44,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: accent,
                }}
              />

              <Typography
                component="p"
                sx={{
                  fontFamily: "Kalam, cursive",
                  fontSize: { xs: 15.5, md: 18 },
                  color: "#3a3a3a",
                  lineHeight: 1.85,
                  letterSpacing: "0.2px",
                  mt: 2.5,
                  mb: 0,
                  "&::first-letter": {
                    float: "left",
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 700,
                    fontSize: "3.2em",
                    lineHeight: 0.82,
                    paddingRight: "10px",
                    marginTop: "4px",
                    color: accent,
                  },
                }}
              >
                {current.moreDescription}
              </Typography>

              {/* Footer - progress */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "rgba(0,0,0,0.08)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${progressPct}%`,
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${accent}, ${accent}aa)`,
                      transition: "width 0.4s ease",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#9a9a9a",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: accent, fontWeight: 700 }}
                  >
                    {chapterNo}
                  </Box>{" "}
                  / {String(total).padStart(2, "0")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grow>
      </Modal>
    </Box>
  );
};

export default EmploymentTimeline;
